const INVETERATE_API_KEY = import.meta.env.PUBLIC_INVETERATE_API_KEY;
const INVETERATE_BASE_URL = 'https://public.inveterateapi.com/v2.0/storefront'; // Updated to correct base URL

// Helper function to make Inveterate API calls
const callInveterateAPI = async (endpoint, options = {}) => {
    if (!INVETERATE_API_KEY) {
        throw new Error('Inveterate API key not configured');
    }

    const defaultHeaders = {
        'X-Inveterate-Api-Key': INVETERATE_API_KEY,
        'accept': 'application/json',
        'Content-Type': 'application/json',
    };

    const config = {
        method: 'GET',
        headers: { ...defaultHeaders, ...options.headers },
        ...options,
    };

    try {
        const response = await fetch(`${INVETERATE_BASE_URL}${endpoint}`, config);

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Inveterate API error: ${response.status} ${response.statusText} - ${errorText}`);
        }

        return await response.json();
    } catch (error) {
        throw error;
    }
};

// Get member data and available credits from Inveterate
export const getInveterateMember = async (memberId) => {
    // Ensure memberId is a string and not empty
    if (!memberId || memberId.toString().trim() === '') {
        throw new Error('Member ID is required and cannot be empty');
    }

    // Convert to string and trim any whitespace
    const cleanMemberId = memberId.toString().trim();

    return callInveterateAPI(`/members/${cleanMemberId}`);
};

// Redeem credits through Inveterate
export const redeemInveterateCredits = async (memberId, redemptionData) => {
    // Ensure memberId is a string and not empty
    if (!memberId || memberId.toString().trim() === '') {
        throw new Error('Member ID is required and cannot be empty');
    }

    // Convert to string and trim any whitespace
    const cleanMemberId = memberId.toString().trim();

    return callInveterateAPI(`/members/${cleanMemberId}/credits/redemption`, {
        method: 'POST',
        body: JSON.stringify(redemptionData),
    });
};

// Helper function to redeem maximum available credits
export const redeemMaxCredits = async (memberId, note = 'Cart redemption') => {
    try {
        // First get the member data to see available credits
        const memberData = await getInveterateMember(memberId);
        const availableCredits = memberData.data.credit;

        if (availableCredits <= 0) {
            throw new Error('No credits available for redemption');
        }

        // Redeem the maximum available credits
        const redemptionData = {
            amount: availableCredits,
            note: note,
            force: true
        };

        const redemptionResponse = await redeemInveterateCredits(memberId, redemptionData);
        return redemptionResponse;
    } catch (error) {
        throw error;
    }
};

// Helper function to redeem credits with cart context awareness
export const redeemCreditsWithCartContext = async (memberId, requestedAmount, cartSubtotal, note = 'Cart redemption') => {
    try {
        // First get the member data to validate available credits
        const memberData = await getInveterateMember(memberId);
        const availableCredits = memberData.data.credit;

        if (requestedAmount > availableCredits) {
            throw new Error(`Requested amount (${requestedAmount}) exceeds available credits (${availableCredits})`);
        }

        if (requestedAmount <= 0) {
            throw new Error('Redemption amount must be greater than 0');
        }

        // If the requested amount is greater than cart subtotal, we might need to handle this differently
        if (requestedAmount > cartSubtotal) {
            // Requested redemption amount exceeds cart subtotal
            // For now, we'll still try to redeem the full amount and let Inveterate handle the limitation
            // In the future, we might want to implement a strategy to handle the excess credits
        }

        // Prepare cart information for Inveterate API
        const cartInfo = {
            subtotal: cartSubtotal,
            requestedAmount: requestedAmount
        };

        // Use the existing redeemSpecificCredits function with cart info
        return await redeemSpecificCredits(memberId, requestedAmount, note, cartInfo);
    } catch (error) {
        throw error;
    }
};

// Helper function to redeem a specific amount of credits
export const redeemSpecificCredits = async (memberId, amount, note = 'Cart redemption', cartInfo = null) => {
    try {
        // First get the member data to validate available credits
        const memberData = await getInveterateMember(memberId);
        const availableCredits = memberData.data.credit;

        if (amount > availableCredits) {
            throw new Error(`Requested amount (${amount}) exceeds available credits (${availableCredits})`);
        }

        if (amount <= 0) {
            throw new Error('Redemption amount must be greater than 0');
        }

        // Redeem the specified amount
        const redemptionData = {
            amount: amount,
            note: note,
            force: true
        };

        const redemptionResponse = await redeemInveterateCredits(memberId, redemptionData);
        return redemptionResponse;
    } catch (error) {
        throw error;
    }
};

// Validate Inveterate API connection (using a test endpoint)
export const validateInveterateConnection = async () => {
    try {
        // Try to get member data with a real member ID to validate connection
        // We'll use a test call that should work if the API is reachable
        const testResponse = await fetch(`${INVETERATE_BASE_URL}/members/2295043129406`, {
            method: 'GET',
            headers: {
                'X-Inveterate-Api-Key': INVETERATE_API_KEY,
                'accept': 'application/json',
                'Content-Type': 'application/json',
            }
        });

        if (testResponse.ok) {
            return { valid: true, message: 'Inveterate API connection successful' };
        } else {
            const errorText = await testResponse.text();
            return { valid: false, message: `Inveterate API error: ${testResponse.status} ${testResponse.statusText} - ${errorText}` };
        }
    } catch (error) {
        // If we get a network error, the API is not reachable
        if (error.message.includes('Failed to fetch')) {
            return { valid: false, message: 'Inveterate API not reachable - check network connection and CORS settings' };
        } else {
            return { valid: false, message: `Inveterate API error: ${error.message}` };
        }
    }
};

export default {
    getInveterateMember,
    redeemInveterateCredits,
    redeemMaxCredits,
    redeemSpecificCredits,
    redeemCreditsWithCartContext,
    validateInveterateConnection,
};
